import { FunctionComponent, ReactNode } from 'react'
import { useTheme } from '@emotion/react'
import { useTranslation } from 'react-i18next'
import GuestCheckoutFooter from '@/components/GuestCheckout/GuestCheckoutFooter'
import GuestCheckoutHeader from '@/components/GuestCheckout/GuestCheckoutHeader'
import GuestInfoSection, { GuestInfoFormFields } from '@/components/GuestCheckout/GuestInfoSection'
import { Venue } from '@/libs/helpers/adapters'
import { GuestInformation } from '@/libs/helpers/apiClient'
import { formatPhone } from '@/libs/helpers/formatters'
import { useWindowSize } from '@/libs/helpers/hooks/useWindowSize'
import { OrderMethod } from '@/libs/helpers/utils'
import { useModal } from '@/providers/modal'
import { Button, Grid, SvgIcon, Text } from '@/tbui'
import { mapFormFieldToGuest } from './GuestCheckout/CheckoutAsGuestModal'

interface GuestInfoProps {
  guest: GuestInformation
  venue: Venue
  handleUpdateMe: (guest: GuestInformation) => Promise<void>
  orderMethod: OrderMethod
}

const GuestInfo: FunctionComponent<GuestInfoProps> = ({
  guest,
  venue,
  handleUpdateMe,
  orderMethod,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { openModal, closeModal } = useModal()
  const windowSize = useWindowSize()

  const handleEditGuestInfo = (validatedFormFields: GuestInfoFormFields): void => {
    const guest = mapFormFieldToGuest(validatedFormFields)
    closeModal()
    handleUpdateMe(guest)
  }

  const openCheckoutAsGuestModal = (): void => {
    openModal(
      <Grid gap="1.75rem" data-test="checkout-as-guest-edit-container">
        <GuestCheckoutHeader name={venue.name} branding={venue.branding} />
        <GuestInfoSection
          title={t('guest_checkout.edit_guest_information')}
          guest={guest}
          submit={{ text: t('save'), callback: handleEditGuestInfo, showAgreement: false }}
          isLoading={false}
          orderMethod={orderMethod}
          venueXRefID={venue.venueXRefID}
        />
        <GuestCheckoutFooter />
      </Grid>,
      {
        onClose: () => closeModal(),
        scrollBehavior: 'SCROLL',
      }
    )
  }

  const renderEditButton = (): ReactNode => (
    <Button
      variant="OUTLINE"
      onClick={openCheckoutAsGuestModal}
      type="button"
      data-test="edit-guest-info"
      center
    >
      <div css={{ display: 'flex', alignItems: 'center' }}>
        <SvgIcon name="edit" fontSize="21px" color="PRIMARY" style={{ marginRight: '0.5rem' }} />
        {t('order_method_selector.edit')}
      </div>
    </Button>
  )

  return (
    <Grid data-test="checkout-guest-information" container gap=".75rem">
      <Grid xs="auto" sm="1fr auto" align="center">
        <Text type="H3">{t('guest_checkout.guest_information')}</Text>
        {windowSize.width > theme.breakpointValues.SM && renderEditButton()}
      </Grid>
      <Grid container gap="5px">
        <Text color="TEXT" data-test="edit-guest-name">
          {guest.firstName} {guest.lastName}
        </Text>
        <Text color="TEXT" data-test="edit-guest-email">
          {guest.email}
        </Text>
        <Grid container template="auto 1fr" align="center">
          <Text color="TEXT" data-test="edit-guest-phone">
            {formatPhone(guest.phone)}&nbsp;
          </Text>
          <Text color="TEXT_2" type="H5" data-test="edit-guest-sms">
            {`- ${
              guest.smsConsent
                ? t('guest_checkout.sms_notification_on')
                : t('guest_checkout.sms_notification_off')
            }`}
          </Text>
        </Grid>
      </Grid>
      {windowSize.width <= theme.breakpointValues.SM && renderEditButton()}
    </Grid>
  )
}

export default GuestInfo
